/**
 * Local admin session vault for PIN fast-switch (see docs/ADMIN_PIN_LOGIN_SPEC.md).
 *
 * Holds snapshots of previously authenticated admin sessions in a dedicated
 * localStorage key (separate from the Supabase SDK's own `sb-<ref>-auth-token`),
 * so a second admin can be re-activated on a shared front-desk device with
 * `supabase.auth.setSession(...)` after a PIN check — without a full password
 * login.
 *
 * Security model (faithful to the spec):
 *  - Only the ACTIVE Supabase session is auto-refreshed. Vault entries are
 *    one-shot snapshots; a dormant snapshot whose refresh token has expired
 *    fails closed → that admin must password-login again.
 *  - Capture is gated behind a per-DEVICE opt-in flag (default OFF). Token
 *    snapshots therefore never accumulate on ordinary admin laptops — only on
 *    enrolled reception devices. This keeps the accepted blast-radius increase
 *    confined to controlled kiosks.
 *  - The vaulted display fields (name/email/access_level) are hints for the
 *    switch UI only; authorization always comes from the live session + role
 *    hooks after a switch.
 */

import type { Session } from '@supabase/supabase-js';
import { supabase } from './supabase';
import {
  ADMIN_SESSION_VAULT_KEY,
  PIN_SWITCH_DEVICE_FLAG_KEY,
  PIN_SWITCH_LOCK_KEY,
} from './constants';

export interface VaultEntry {
  adminId: string;
  accessToken: string;
  refreshToken: string;
  fullName: string | null;
  email: string | null;
  accessLevel: string | null;
  /** Set when re-activation failed (expired/invalid refresh token). */
  stale?: boolean;
  /** ms epoch of last update; newest-active wins on cross-tab merges. */
  updatedAt: number;
}

type VaultMap = Record<string, VaultEntry>;
type Listener = () => void;

const hasWindow = typeof window !== 'undefined';
const BROADCAST_NAME = 'hok-os-admin-vault';
const EMPTY: VaultEntry[] = [];

const listeners = new Set<Listener>();
let channel: BroadcastChannel | null = null;
let initialized = false;
/** The admin id of the session currently active in this tab's Supabase client. */
let activeAdminId: string | null = null;
/**
 * Cached, referentially-stable snapshot for useSyncExternalStore. Recomputed
 * only when the vault changes, so getSnapshot returns the same array between
 * changes (a fresh array every call would loop the store).
 */
let snapshotCache: VaultEntry[] | null = null;

// ---------------------------------------------------------------------------
// Device opt-in
// ---------------------------------------------------------------------------

/** True when this browser/device has opted in to PIN fast-switch. */
export function isPinSwitchDeviceEnabled(): boolean {
  if (!hasWindow) return false;
  try {
    return window.localStorage.getItem(PIN_SWITCH_DEVICE_FLAG_KEY) === 'on';
  } catch {
    return false;
  }
}

/** Enrol this device: capture begins on the next auth event. */
export function enablePinSwitchOnDevice(): void {
  if (!hasWindow) return;
  try {
    window.localStorage.setItem(PIN_SWITCH_DEVICE_FLAG_KEY, 'on');
  } catch {
    /* ignore quota / disabled storage */
  }
}

/** Un-enrol this device and wipe every vaulted session from it. */
export function disablePinSwitchOnDevice(): void {
  if (!hasWindow) return;
  try {
    window.localStorage.removeItem(PIN_SWITCH_DEVICE_FLAG_KEY);
  } catch {
    /* ignore */
  }
  unlockDevice(); // never leave a disabled device stuck on the lock screen
  clearVault();
}

// ---------------------------------------------------------------------------
// Lock state — the enforcement backing the switch/idle "lock". Persisted so it
// survives the reload, and broadcast so it holds across tabs. ProtectedRoute
// refuses app routes while locked; a successful switch/login clears it.
// ---------------------------------------------------------------------------

export function isDeviceLocked(): boolean {
  if (!hasWindow) return false;
  try {
    return window.localStorage.getItem(PIN_SWITCH_LOCK_KEY) === '1';
  } catch {
    return false;
  }
}

export function lockDevice(): void {
  if (!hasWindow) return;
  try {
    window.localStorage.setItem(PIN_SWITCH_LOCK_KEY, '1');
  } catch {
    /* ignore */
  }
  notify(true);
}

export function unlockDevice(): void {
  if (!hasWindow) return;
  try {
    window.localStorage.removeItem(PIN_SWITCH_LOCK_KEY);
  } catch {
    /* ignore */
  }
  notify(true);
}

// ---------------------------------------------------------------------------
// Storage primitives
// ---------------------------------------------------------------------------

function readVault(): VaultMap {
  if (!hasWindow) return {};
  try {
    const raw = window.localStorage.getItem(ADMIN_SESSION_VAULT_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as VaultMap;
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function writeVault(map: VaultMap): void {
  if (!hasWindow) return;
  try {
    window.localStorage.setItem(ADMIN_SESSION_VAULT_KEY, JSON.stringify(map));
  } catch {
    /* ignore quota / disabled storage */
  }
  notify(true); // local mutation → tell other tabs too
}

/**
 * Recompute the cached snapshot and fan out to listeners.
 *
 * @param broadcast post to other tabs. True only for LOCAL mutations; remote
 * notifications pass false so a received broadcast doesn't echo back and
 * ping-pong between tabs.
 */
function notify(broadcast: boolean): void {
  snapshotCache = Object.values(readVault()).sort((a, b) => b.updatedAt - a.updatedAt);
  if (broadcast) {
    try {
      channel?.postMessage('changed');
    } catch {
      /* channel may be closed */
    }
  }
  for (const l of listeners) {
    try {
      l();
    } catch {
      /* a bad listener must not break the others */
    }
  }
}

// ---------------------------------------------------------------------------
// Public read API
// ---------------------------------------------------------------------------

/**
 * All vaulted entries, newest-active first. Returns a referentially-stable
 * array between changes (safe as a useSyncExternalStore getSnapshot).
 */
export function listVaultEntries(): VaultEntry[] {
  if (!hasWindow) return EMPTY;
  if (snapshotCache === null) {
    snapshotCache = Object.values(readVault()).sort((a, b) => b.updatedAt - a.updatedAt);
  }
  return snapshotCache;
}

export function getVaultEntry(adminId: string): VaultEntry | null {
  return readVault()[adminId] ?? null;
}

export function getActiveAdminId(): string | null {
  return activeAdminId;
}

/** Subscribe to vault changes (this tab + cross-tab). Returns an unsubscribe. */
export function subscribeVault(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

// ---------------------------------------------------------------------------
// Public mutation API
// ---------------------------------------------------------------------------

export function markVaultEntryStale(adminId: string): void {
  const map = readVault();
  const entry = map[adminId];
  if (!entry) return;
  map[adminId] = { ...entry, stale: true, updatedAt: Date.now() };
  writeVault(map);
}

export function removeVaultEntry(adminId: string): void {
  const map = readVault();
  if (!map[adminId]) return;
  delete map[adminId];
  writeVault(map);
}

/** Remove every vaulted session from this device (clear-device / sign-out-all). */
export function clearVault(): void {
  if (!hasWindow) return;
  try {
    window.localStorage.removeItem(ADMIN_SESSION_VAULT_KEY);
  } catch {
    /* ignore */
  }
  notify(true);
}

// ---------------------------------------------------------------------------
// Capture (auth-event sync)
// ---------------------------------------------------------------------------

async function fetchAdminProfile(
  adminId: string,
): Promise<{ full_name: string | null; email: string | null; access_level: string | null } | null> {
  const { data, error } = await supabase
    .from('admin_users')
    .select('full_name, email, access_level')
    .eq('id', adminId)
    .maybeSingle();
  if (error || !data) return null;
  return data;
}

/**
 * Snapshot the active session into the vault. Only admins are vaulted: a session
 * whose user isn't in admin_users is ignored (which also guards against vaulting
 * a non-admin). Existing display fields are reused on a token refresh so we don't
 * re-query admin_users every hour.
 */
async function captureActiveSession(session: Session): Promise<void> {
  const adminId = session.user.id;
  activeAdminId = adminId;

  const existing = getVaultEntry(adminId);
  let profile = existing
    ? {
        full_name: existing.fullName,
        email: existing.email,
        access_level: existing.accessLevel,
      }
    : null;

  if (!profile) {
    profile = await fetchAdminProfile(adminId);
    if (!profile) return; // not an admin → never vault
  }

  // Re-read AFTER the await so a concurrent capture that landed during the
  // profile fetch isn't clobbered (read-modify-write on the freshest map).
  const map = readVault();
  map[adminId] = {
    adminId,
    accessToken: session.access_token,
    refreshToken: session.refresh_token,
    fullName: profile.full_name,
    email: profile.email ?? session.user.email ?? null,
    accessLevel: profile.access_level,
    stale: false,
    updatedAt: Date.now(),
  };
  writeVault(map);
}

/**
 * Snapshot the current session immediately (used right after a device opts in,
 * so the active admin is vaulted without waiting for the next auth event).
 */
export async function captureCurrentSession(): Promise<void> {
  if (!isPinSwitchDeviceEnabled()) return;
  const { data } = await supabase.auth.getSession();
  if (data.session) await captureActiveSession(data.session);
}

// ---------------------------------------------------------------------------
// Init
// ---------------------------------------------------------------------------

/**
 * Wire the vault to Supabase auth events. Idempotent and a no-op unless this
 * device is enrolled (so ordinary devices never start hoarding tokens). Safe to
 * call once at app start.
 */
export function initSessionVault(): void {
  if (initialized || !hasWindow) return;
  if (!isPinSwitchDeviceEnabled()) return;
  initialized = true;

  // Cross-tab: a change in another tab updates this tab's listeners. Remote
  // notifications pass broadcast=false so they don't echo back (no ping-pong).
  try {
    channel = new BroadcastChannel(BROADCAST_NAME);
    channel.onmessage = () => notify(false);
  } catch {
    channel = null; // BroadcastChannel unsupported — fall back to storage events
  }
  if (!channel) {
    window.addEventListener('storage', (e) => {
      if (e.key === ADMIN_SESSION_VAULT_KEY || e.key === PIN_SWITCH_LOCK_KEY) notify(false);
    });
  }

  supabase.auth.onAuthStateChange((event, session) => {
    if (event === 'SIGNED_OUT') {
      // Intentional sign-out (lock never calls signOut, per the invariants).
      if (activeAdminId) removeVaultEntry(activeAdminId);
      activeAdminId = null;
      return;
    }
    if (
      session &&
      (event === 'INITIAL_SESSION' ||
        event === 'SIGNED_IN' ||
        event === 'TOKEN_REFRESHED' ||
        event === 'USER_UPDATED')
    ) {
      void captureActiveSession(session);
    }
  });
}
